import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useInView } from "react-intersection-observer";
import { useIsFetching } from "@tanstack/react-query";
import { ContentCard } from "@companion-core/shared/app/Components/Cards/ContentCard";
import { ComponentCardProps } from "@companion-core/shared/app/Components/Cards/ComponentCard/ComponentCard.model";
import { useComponentCard } from "@companion-core/shared/app/Components/Cards/ComponentCard/hooks/componentCard";
import { useI18nStore } from "@companion-core/shared/app/Store/i18nStore";
import "Assets/Styles/scss/componentCard.scss";

export const ComponentCard = ({
  type,
  component,
  pageSlug,
  chapters,
  nbItems,
  position,
  url,
  blockItemId,
  variant = "default",
  fixedHeight = false,
  tags,
  titleVariant,
  redirectQueryParams,
}: ComponentCardProps) => {
  const { locale: lang } = useI18nStore((state) => ({ locale: state.locale }));
  const [params] = useSearchParams();
  const {
    componentId,
    componentTitle,
    componentSubtitle,
    componentImageUrl,
    componentImageAlt,
    componentLogoUrl,
    componentLogoAlt,
    componentDate,
    componentDetailUrl,
    onComponentCardVisible,
    onComponentCardVisited,
  } = useComponentCard({
    component,
    pageSlug,
    chapters,
    nbItems,
    position,
    url,
    variant,
    redirectQueryParams,
  });
  const isFetchingBlocks = useIsFetching({
    queryKey: ["blocks"],
  });
  const isFetchingBlockItems = useIsFetching({
    queryKey: ["block-items"],
  });
  const isFetchingComponents = useIsFetching({
    queryKey: ["block-item-components", 15, blockItemId, lang, params],
  });
  const isPageLoaded = useMemo(
    () => !(isFetchingBlocks || isFetchingBlockItems || isFetchingComponents),
    [isFetchingBlocks, isFetchingBlockItems, isFetchingComponents]
  );
  const [isAlreadyTracked, setIsAlreadyTracked] = useState<boolean>(false);
  const { ref, inView } = useInView({
    /* Optional options */
    threshold: 0.8,
    triggerOnce: true,
    skip: !isPageLoaded || isAlreadyTracked,
  });

  useEffect(() => {
    if (inView && onComponentCardVisible && !isAlreadyTracked) {
      onComponentCardVisible();
      if (!isAlreadyTracked) setIsAlreadyTracked(true);
    }
  }, [inView, onComponentCardVisible]);

  return (
    <Link
      ref={ref}
      data-testid={`component-card--${componentId}--link`}
      to={componentDetailUrl}
      state={{ canGoBack: true }}
      className={`component-card ${inView && isPageLoaded ? "component-card--visible" : ""}`}
      onClick={onComponentCardVisited}>
      <ContentCard
        testID={`component-card--${componentId}`}
        title={componentTitle}
        subtitle={componentSubtitle}
        imageUrl={componentImageUrl}
        imageAlt={componentImageAlt}
        logo={componentLogoUrl}
        logoAlt={componentLogoAlt}
        date={componentDate}
        type={type}
        fixedHeight={fixedHeight}
        tags={tags}
        titleVariant={titleVariant}
      />
    </Link>
  );
};
